import type { ColumnDef } from "@tanstack/react-table";
import { ArrowBigRight, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SimpleTable } from "~/components/common/simple-table";
import { SearchHeader } from "~/components/common/table-components";
import { Button, Input } from "~/components/ui";
import type { TagAttributeDto } from "~/types/api";
import { normalizeAttributeName } from "~/utils/coldefs/node";
import { defaultTableColumns } from "~/utils/table-util";

export default function AttributeFormField({
  value,
  onChange,
  options = [],
  deveui,
}: {
  value: TagAttributeDto[];
  onChange: (val: TagAttributeDto[]) => void;
  options?: string[];
  deveui: string;
}) {
  const { t } = useTranslation();

  async function handleAdd(attribute: TagAttributeDto) {
    onChange(value.concat(attribute));
  }

  async function handleRemove(attributes: TagAttributeDto[]) {
    const removingKeys = attributes.map((x) => x.normalizedName);
    const modifiedValue = value.filter(
      (x) => !removingKeys.includes(x.normalizedName),
    );
    onChange(modifiedValue);
  }

  const colDef: ColumnDef<any>[] = [
    defaultTableColumns["select"],
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <SearchHeader column={column} child="tag.fields.name" />
      ),
      cell: (info) => info.getValue(),
    },
    {
      id: "value",
      accessorKey: "value",
      header: t("tagKey.fields.value"),
      cell: (info) => info.getValue(),
    },
  ];

  return (
    <div className="ml-3">
      <AttributeCreate options={options} onAdd={handleAdd} tagId={deveui} />
      <SimpleTable
        data={value}
        colDef={colDef}
        pageSizes={[10, 25]}
        onRemove={handleRemove}
        removable
      />
    </div>
  );
}

export function AttributeCreate({
  options = [],
  onAdd,
  tagId,
}: {
  options?: string[];
  onAdd: (val: TagAttributeDto) => void;
  tagId: string;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [filtered, setFiltered] = useState<string[]>([]);

  async function handleAdd() {
    if (name && value) {
      onAdd({
        tagId,
        name: name,
        value: value,
        normalizedName: normalizeAttributeName(name),
      });
      setName("");
      setValue("");
      return;
    }
    toast.error(t("common.errors.missingFields"));
  }

  async function handleChange(val: string) {
    setName(val);
    setFiltered(
      options.filter((o) => o.toLowerCase().startsWith(val.toLowerCase())),
    );
  }

  return (
    <div className="flex">
      <div className="mr-1 w-1/3">
        <Input
          value={name ?? ""}
          placeholder={t("tag.placeholders.name")}
          onChange={(e) => handleChange(e.target.value)}
          list="attributeOptions"
        />
        {filtered.length > 0 && (
          <datalist id="attributeOptions">
            {filtered.map((opt) => (
              <option value={opt} key={"attributeOption-" + opt} />
            ))}
          </datalist>
        )}
      </div>
      <ArrowBigRight className="pt-1 mr-1" />
      <div className="mr-2 w-1/3">
        <Input
          value={value ?? ""}
          placeholder={t("tagKey.placeholders.value")}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <div className="w-1/3">
        <Button
          variant="ghost"
          aria-label="Add Selected"
          onClick={handleAdd}
          type="button"
        >
          <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 " />
        </Button>
      </div>
    </div>
  );
}
